import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, MutationCache, QueryCache, onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Network } from '@capacitor/network';
import { isCapacitor } from './shared/api/config';
// Import self-hosted fonts (replaces Google Fonts CDN — CASA DAST #4)
import './fonts.css';
// Import global styles (Tailwind CSS)
import './globals.css';

// Initialize i18n before App
import './i18n/config';

import App from './App';
import { getPersistOptions } from './shared/offline/queryPersister';
import { offlineStorage, OfflineStorageService } from './shared/offline/OfflineStorageService';
import { handleMutationError, handleQueryError } from './shared/utils/globalErrorToasts';
import { shouldRetryQuery } from './shared/utils/queryRetry';
import { cleanupFossilServiceWorkers } from './shared/utils/cleanupFossilServiceWorkers';
import { maybeRedirectToWorkspaceDomain } from './shared/utils/workspaceDomainRedirect';
import { maybeRedirectToShopifyAppEntry } from './shared/utils/shopifyAppEntry';
import { initLiveUpdates } from './shared/ota/liveUpdate';

// Configure QueryClient with offline support and global error handling.
// The mutation/query error → toast handlers live in ./shared/utils/globalErrorToasts
// (extracted so their suppression + dedupe logic is unit-testable).
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: handleMutationError,
  }),
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  defaultOptions: {
    queries: {
      staleTime: 0, // Always refetch for fresh data in collaborative app
      gcTime: 24 * 60 * 60 * 1000, // 24 hours (keep cache for offline fallback)
      retry: shouldRetryQuery,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      networkMode: 'online', // Mutations require network in Phase 1
      retry: false,
    },
  },
});

// On Capacitor, navigator.onLine in the WebView is unreliable — it can report
// "online" while the device's actual radio is off (or vice versa). React Query's
// default OnlineManager subscribes to window 'online'/'offline' events that
// follow that same unreliable signal, which means queries fire fetch() against
// a dead network, get TypeError, flip to error state, and the UI shows "Failed
// to load X" even when the cached data is right there in IndexedDB.
//
// Authoritative source is the native side. Hook Capacitor's Network plugin into
// the OnlineManager so React Query trusts the device, not the WebView.
if (isCapacitor) {
  onlineManager.setEventListener((setOnline) => {
    let cancelled = false;
    let removeListener: (() => void) | null = null;

    Network.getStatus().then((status) => {
      if (!cancelled) setOnline(status.connected);
    });
    Network.addListener('networkStatusChange', (status) => {
      setOnline(status.connected);
    }).then((handle) => {
      if (cancelled) {
        handle.remove();
      } else {
        removeListener = () => handle.remove();
      }
    });

    return () => {
      cancelled = true;
      if (removeListener) removeListener();
    };
  });
}

// Self-heal: remove any "fossil" service worker + its caches left by old builds.
// PanLuma ships no service worker on web (offline is data-only via IndexedDB).
// Fire-and-forget — the utility never rejects, but be explicit about it.
cleanupFossilServiceWorkers().catch(() => {});

// OTA live updates (native bundled app only). Confirms the running bundle is
// good (rollback guard) and checks the channel manifest for a newer web bundle,
// staging it for the next launch — all under the single bundled origin so the
// offline data layer is untouched. No-op on web and local-dev. Fire-and-forget.
initLiveUpdates().catch(() => {});

// Initialize offline storage
if (OfflineStorageService.isAvailable()) {
  offlineStorage.initialize().catch((error) => {
    console.error('Failed to initialize offline storage:', error);
  });
}

function renderApp(): void {
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <React.StrictMode>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={getPersistOptions()}
      >
        <App />
      </PersistQueryClientProvider>
    </React.StrictMode>
  );
}

// Shopify opens the app at its App URL; when that is the bare canonical host
// (`app.panluma.ai/?shop=…&hmac=…`), CloudFront serves the plain SPA and the
// install would dead-end on the home page. Hand a genuine Shopify App-URL load
// off to the backend install entry (HMAC-verify → OAuth / embedded App Home)
// before rendering. Returns false (render proceeds) for every normal visit.
if (!maybeRedirectToShopifyAppEntry()) {
  // A canonical-host email entity link opened in a desktop browser may carry a
  // `?domain=` hint for a custom-domain tenant — redirect to that (validated)
  // workspace origin before rendering so the user lands where their session is.
  // Resolves false (no redirect) for the common no-hint case, so render proceeds.
  maybeRedirectToWorkspaceDomain()
    .then((redirecting) => {
      if (!redirecting) renderApp();
    })
    .catch(() => renderApp());
}
